import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, inject } from "@angular/core";

import { SIN_DATO, entero, pct } from "../cc-formato";
import { MS_CIFRA, cuenta } from "./movimiento-cc";

/** Un porcentaje chico con hasta dos decimales, como lo enseña SonarQube en la duplicación: "0.34 %", "2.0 %". */
const HASTA_DOS = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

/**
 * Una cifra clave con conteo ascendente breve (≤ 600 ms) al entrar o al
 * cambiar: "0 %" → "98.5 %". Escribe el texto directo en su elemento (sin
 * detección de cambios por cuadro). Sin valor pinta «—»; con
 * prefers-reduced-motion pinta el final de inmediato.
 */
@Component({
  selector: "app-cifra-cc",
  standalone: true,
  template: "",
  host: { class: "cc-num" },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CifraCcComponent implements OnChanges, OnDestroy {
  /** El valor final (crudo de SonarQube o número). */
  @Input() valor: string | number | null | undefined = null;
  /** PCT: un decimal y %; PCT2: hasta dos decimales y % (la duplicación); ENTERO: miles con coma. */
  @Input() formato: "PCT" | "PCT2" | "ENTERO" = "ENTERO";

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private cancela: () => void = () => undefined;
  private actual = 0;

  ngOnChanges(): void {
    this.cancela();
    const n = this.valor == null || this.valor === "" ? null : Number(this.valor);
    if (n == null || !Number.isFinite(n)) {
      this.actual = 0;
      this.host.nativeElement.textContent = SIN_DATO;
      return;
    }
    this.cancela = cuenta(this.actual, n, MS_CIFRA, (v) => this.pinta(v));
    this.actual = n;
  }

  ngOnDestroy(): void {
    this.cancela();
  }

  private pinta(v: number): void {
    this.host.nativeElement.textContent = textoCifra(v, this.formato);
  }
}

/** El texto de una cifra en su formato (también los cuadros del conteo). */
export function textoCifra(v: number, formato: "PCT" | "PCT2" | "ENTERO"): string {
  if (formato === "PCT2") {
    return `${HASTA_DOS.format(v)}\u202F%`;
  }
  return formato === "PCT" ? pct(v) : entero(v);
}
